import {useCallback, useState} from 'react';
import getStudyVideoPath from '../../helper/firebase/storage/getStudyVideoPath';
import uploadStorageFile from '../../helper/firebase/storage/uploadStorageFile';
import useFilePaste from '../../bonds/bondImageUpload/useFilePaste';

export interface VideoSource {
  src: string;
  type: string;
  resolution?: string;
  media?: string;
}

export interface UseVideoUploadOptions {
  moduleId: string;
  topic: string;
  maxSizeBytes?: number;
  onUploadComplete?: (markdown: string) => void;
  onError?: (error: string) => void;
}

export interface UseVideoUploadReturn {
  uploadVideo: (file: File) => Promise<string | undefined>;
  uploadMultipleVideos: (files: File[]) => Promise<VideoSource[] | undefined>;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleMultipleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleYouTubeLink: (url: string) => void;
  handleRumbleLink: (url: string) => Promise<void>;
  uploading: boolean;
  error: string | null;
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * Supports regular videos, Shorts, and embed URLs
 */
function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    // YouTube Shorts: youtube.com/shorts/VIDEO_ID
    /youtube\.com\/shorts\/([^&\n?#\/]+)/,
    // Regular videos: youtube.com/watch?v=VIDEO_ID or youtu.be/VIDEO_ID or youtube.com/embed/VIDEO_ID
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    // Fallback: youtube.com/watch?.*v=VIDEO_ID
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Generates YouTube embed markdown
 */
function generateYouTubeMarkdown(videoId: string): string {
  return `\n\n<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>\n\n`;
}

/**
 * Extracts Rumble video ID from Rumble URL formats
 * Supports regular URLs like: rumble.com/v740lc8-title.html
 * and embed URLs like: rumble.com/embed/v740lc8/
 */
function extractRumbleVideoId(url: string): string | null {
  const patterns = [
    // Rumble embed: rumble.com/embed/v740lc8/ or rumble.com/embed/v740lc8
    /rumble\.com\/embed\/(v[a-z0-9]+)/i,
    // Rumble regular: rumble.com/v740lc8-title.html or rumble.com/v740lc8
    // Match v followed by alphanumeric characters (capture includes the 'v')
    /rumble\.com\/(v[a-z0-9]+)/i,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Generates Rumble embed markdown
 */
function generateRumbleMarkdown(videoId: string): string {
  return `\n\n<iframe width="560" height="315" src="https://rumble.com/embed/${videoId}/" frameborder="0" allowfullscreen></iframe>\n\n`;
}

/**
 * Detects video resolution from filename or returns a default
 * Supports common naming patterns: video-1080p.mp4, video_720p.mp4, etc.
 */
function detectResolution(filename: string): string | undefined {
  const resolutionMatch = filename.match(/(\d+)p/i);
  if (resolutionMatch) {
    return resolutionMatch[1];
  }
  return undefined;
}

/**
 * Generates media query for a resolution
 * Higher resolutions get more restrictive media queries
 */
function getMediaQuery(resolution: string | undefined): string | undefined {
  if (!resolution) return undefined;
  const height = Number.parseInt(resolution, 10);
  if (Number.isNaN(height)) return undefined;

  // Media queries based on resolution
  // 1080p and above: min-width 1920px
  // 720p: min-width 1280px
  // 480p: min-width 768px
  // Below 480p: no media query (default/fallback)
  if (height >= 1080) {
    return '(min-width: 1920px)';
  }
  if (height >= 720) {
    return '(min-width: 1280px)';
  }
  if (height >= 480) {
    return '(min-width: 768px)';
  }
  return undefined;
}

/**
 * Generates video markdown with multiple sources
 */
function generateVideoMarkdown(sources: VideoSource[]): string {
  if (sources.length === 0) return '';

  const sourceElements = sources
    .map((source) => {
      const mediaAttr = source.media ? ` media="${source.media}"` : '';
      return `  <source src="${source.src}" type="${source.type}"${mediaAttr}>`;
    })
    .join('\n');

  return `\n\n<video controls width="100%">\n${sourceElements}\n  Your browser does not support the video tag.\n</video>\n\n`;
}

/**
 * Hook to handle video uploads to Firebase Storage and YouTube link embedding
 * Supports file selection via input, paste functionality for files, and YouTube link detection
 * Now supports multiple video resolutions with automatic selection
 */
export default function useVideoUpload({
  moduleId,
  topic,
  maxSizeBytes = 100 * 1024 * 1024, // 100 MB default for videos
  onUploadComplete,
  onError,
}: UseVideoUploadOptions): UseVideoUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadVideo = useCallback(
    async (file: File): Promise<string | undefined> => {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        const errorMsg = 'Please select a video file';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate file size
      if (file.size > maxSizeBytes) {
        const errorMsg = `Video size must be less than ${Math.round(maxSizeBytes / 1024 / 1024)} MB`;
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate moduleId and topic
      if (!moduleId || !topic) {
        const errorMsg = 'Module ID and topic are required';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setError(null);
      setUploading(true);

      try {
        // Generate UUID filename with original extension
        const fileExtension = file.name.split('.').pop() || 'mp4';
        const filename = `${crypto.randomUUID()}.${fileExtension}`;
        const storagePath = getStudyVideoPath(moduleId, topic, filename);

        // Upload file
        const downloadURL = await uploadStorageFile(file, storagePath);

        const videoMarkdown = `\n\n<video controls width="100%">\n  <source src="${downloadURL}" type="${file.type}">\n  Your browser does not support the video tag.\n</video>\n\n`;
        onUploadComplete?.(videoMarkdown);
        setUploading(false);
        return downloadURL;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to upload video';
        setError(errorMsg);
        onError?.(errorMsg);
        setUploading(false);
        return;
      }
    },
    [moduleId, topic, maxSizeBytes, onUploadComplete, onError],
  );

  const uploadMultipleVideos = useCallback(
    async (files: File[]): Promise<VideoSource[] | undefined> => {
      // Validate all files
      const invalidFiles = files.filter((file) => !file.type.startsWith('video/'));
      if (invalidFiles.length > 0) {
        const errorMsg = 'All selected files must be video files';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate file sizes
      const oversizedFiles = files.filter((file) => file.size > maxSizeBytes);
      if (oversizedFiles.length > 0) {
        const errorMsg = `Some videos exceed the size limit of ${Math.round(maxSizeBytes / 1024 / 1024)} MB`;
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      // Validate moduleId and topic
      if (!moduleId || !topic) {
        const errorMsg = 'Module ID and topic are required';
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setError(null);
      setUploading(true);

      try {
        // Upload all files and collect sources
        const sources: VideoSource[] = [];
        const baseId = crypto.randomUUID();

        // Sort files by resolution (highest first) for better source ordering
        const filesWithResolution = files.map((file) => ({
          file,
          resolution: detectResolution(file.name),
        }));

        // Sort: highest resolution first, then files without resolution info
        filesWithResolution.sort((a, b) => {
          if (!a.resolution && !b.resolution) return 0;
          if (!a.resolution) return 1;
          if (!b.resolution) return -1;
          return Number.parseInt(b.resolution, 10) - Number.parseInt(a.resolution, 10);
        });

        // Upload each file
        for (const {file, resolution} of filesWithResolution) {
          const fileExtension = file.name.split('.').pop() || 'mp4';
          const filename = `${baseId}-${resolution ? `${resolution}p` : 'default'}.${fileExtension}`;
          const storagePath = getStudyVideoPath(moduleId, topic, filename);

          const downloadURL = await uploadStorageFile(file, storagePath);
          const media = getMediaQuery(resolution);

          sources.push({
            src: downloadURL,
            type: file.type,
            resolution,
            media,
          });
        }

        // Generate markdown with all sources
        const videoMarkdown = generateVideoMarkdown(sources);
        onUploadComplete?.(videoMarkdown);
        setUploading(false);
        return sources;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to upload videos';
        setError(errorMsg);
        onError?.(errorMsg);
        setUploading(false);
        return;
      }
    },
    [moduleId, topic, maxSizeBytes, onUploadComplete, onError],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      // If multiple files selected, use multiple upload
      if (files.length > 1) {
        await uploadMultipleVideos(Array.from(files));
      } else {
        await uploadVideo(files[0]);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [uploadVideo, uploadMultipleVideos],
  );

  const handleMultipleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      await uploadMultipleVideos(Array.from(files));
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [uploadMultipleVideos],
  );

  const handleYouTubeLink = useCallback(
    (url: string) => {
      const videoId = extractYouTubeVideoId(url);
      if (videoId) {
        setError(null);
        const markdown = generateYouTubeMarkdown(videoId);
        onUploadComplete?.(markdown);
      } else {
        const errorMsg = 'Invalid YouTube URL';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    [onUploadComplete, onError],
  );

  const handleRumbleLink = useCallback(
    async (url: string) => {
      // Try to get embed code from oEmbed API first
      try {
        const encodedUrl = encodeURIComponent(url);
        const oEmbedUrl = `https://rumble.com/api/Media/oembed.json?url=${encodedUrl}`;
        const response = await fetch(oEmbedUrl);

        if (response.ok) {
          const data = await response.json();
          // Extract the embed URL from the HTML
          const embedMatch = data.html?.match(/src="([^"]+)"/);
          if (embedMatch && embedMatch[1]) {
            setError(null);
            const embedUrl = embedMatch[1];
            const markdown = `\n\n<iframe width="560" height="315" src="${embedUrl}" frameborder="0" allowfullscreen></iframe>\n\n`;
            onUploadComplete?.(markdown);
            return;
          }
        }
      } catch {
        // Fall through to direct extraction if API fails
      }

      // Fallback: extract video ID directly from URL
      const videoId = extractRumbleVideoId(url);
      if (videoId) {
        setError(null);
        const markdown = generateRumbleMarkdown(videoId);
        onUploadComplete?.(markdown);
      } else {
        const errorMsg = 'Invalid Rumble URL';
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    [onUploadComplete, onError],
  );

  // Set up paste handler for video files
  useFilePaste({
    onFilePaste: async (file) => {
      if (file.type.startsWith('video/')) {
        const url = await uploadVideo(file);
        if (url) {
          // onUploadComplete is already called in uploadVideo
        }
      }
    },
  });

  return {
    uploadVideo,
    uploadMultipleVideos,
    handleFileSelect,
    handleMultipleFileSelect,
    handleYouTubeLink,
    handleRumbleLink,
    uploading,
    error,
  };
}
